/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const lastUpdatedDate = new Date(1669593600000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: null,
        description: null,
        lastUpdatedDate: "Mon Nov 28 2022 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: "emarsys",
        type: "destination",
        icon: null,
        toc: [{
          "value": "Setup",
          "depth": 0,
          "id": "setup"
        }, {
          "value": "Syncing",
          "depth": 0,
          "id": "syncing"
        }, {
          "value": "Sync modes",
          "depth": 1,
          "id": "sync-modes"
        }, {
          "value": "Record matching",
          "depth": 1,
          "id": "record-matching"
        }, {
          "value": "Fields mapping",
          "depth": 1,
          "id": "fields-mapping"
        }, {
          "value": "Debugging",
          "depth": 0,
          "id": "debugging"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      h2: "h2",
      p: "p",
      code: "code",
      strong: "strong",
      img: "img",
      a: "a",
      h3: "h3",
      ul: "ul",
      li: "li",
      em: "em"
    }, _provideComponents(), props.components), {PremiumDestination} = _components;
    if (!PremiumDestination) _missingMdxReference("PremiumDestination", true);
    return _jsxs(_Fragment, {
      children: [_jsx(PremiumDestination, {
        destinationName: "Emarsys"
      }), "\n", _jsx(_components.h2, {
        id: "setup",
        children: "Setup"
      }), "\n", _jsxs(_components.p, {
        children: ["You will need your username, usually in the format of ", _jsx(_components.code, {
          children: "account_name00X"
        }), " where ", _jsx(_components.code, {
          children: "X"
        }), " is a digit, and the API secret."]
      }), "\n", _jsxs(_components.p, {
        children: ["To get the API secret, you can create a new API user for Hightouch under ", _jsx(_components.strong, {
          children: "Admin"
        }), " > ", _jsx(_components.strong, {
          children: "Security Settings"
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-emarsys-api-settings.png",
          alt: ""
        })
      }), "\n", _jsx(Warning, {
        children: _jsx(_components.p, {
          children: "Make sure to copy and save the secret key here since the entire key will no\nlonger display after you close the confirmation dialog box."
        })
      }), "\n", _jsxs(_components.p, {
        children: ["You will need to enable the necessary endpoints for this API key. For reference, here are the endpoints\nHightouch will use in any given sync.\n", _jsx(_components.img, {
          src: "destinations/destination-emarsys-api-endpoints.png",
          alt: ""
        })]
      }), "\n", _jsxs(_components.p, {
        children: ["You can select all of these endpoints or you can specify specific endpoints that only your\nsync will need. For example, if you do not plan on removing contacts when data leave your warehouse, you do not have to select\nthe ", _jsx(_components.code, {
          children: "contact.delete"
        }), " endpoint. Generally, it's easy and safe to select all ", _jsx(_components.strong, {
          children: "contacts"
        }), ", ", _jsx(_components.strong, {
          children: "fields"
        }), ", and ", _jsx(_components.strong, {
          children: "contact lists"
        }), " endpoints."]
      }), "\n", _jsxs(_components.p, {
        children: ["API users and permissions are described in the ", _jsx(_components.a, {
          href: "https://help.emarsys.com/hc/en-us/articles/115004740329#api-users",
          children: "Emarsys docs"
        }), "."]
      }), "\n", _jsx(_components.h2, {
        id: "syncing",
        children: "Syncing"
      }), "\n", _jsxs(_components.p, {
        children: ["Hightouch supports syncing ", _jsx(_components.strong, {
          children: "contacts"
        }), " and ", _jsx(_components.strong, {
          children: "contact lists"
        }), "."]
      }), "\n", _jsx(_components.h3, {
        id: "sync-modes",
        children: "Sync modes"
      }), "\n", _jsx(_components.p, {
        children: "Here are the possible modes for the Emarsys contacts:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Upsert"
          }), ": pushes new contacts to Emarsys and updates existing contacts with the fields that change in your warehouse."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Update"
          }), ": updates particular fields on existing contacts in Emarsys. It doesn't add new objects."]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["This integration also supports ", _jsx(_components.strong, {
          children: "Segment"
        }), " mode. New users will be inserted into the list.\nUsers that are removed from the model will be removed from the list."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-emarsys-segment-mode.png",
          alt: ""
        })
      }), "\n", _jsx(_components.h3, {
        id: "record-matching",
        children: "Record matching"
      }), "\n", _jsxs(_components.p, {
        children: ["Records can be matched from your source to Emarsys by ", _jsx(_components.em, {
          children: "any"
        }), " default and custom fields. However, it's important to make sure\nthat the field you are using is unique to ensure the best results. For example, Emarsys will throw an error if it detects duplicate\nvalues in an attempt to update or remove a batch of contacts.\nWe recommend using ", _jsx(_components.code, {
          children: "email"
        }), " or creating a custom unique field, like an ", _jsx(_components.code, {
          children: "external_id"
        }), " (make sure to set it as ", _jsx(_components.em, {
          children: "unique"
        }), ")."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-emarsys-record-matching.png",
          alt: ""
        })
      }), "\n", _jsx(_components.h3, {
        id: "fields-mapping",
        children: "Fields mapping"
      }), "\n", _jsx(_components.p, {
        children: "You can also map to any updatable fields in Emarsys, including your custom fields."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-emarsys-fields.png",
          alt: ""
        })
      }), "\n", _jsx(_components.p, {
        children: "However, there are some things to keep in mind:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: ["The Emarsys API uses the field's ID when making requests so if you need to use the JSON editor, you will not see any named fields.\nInstead, you will only see the field ids. You can use this ", _jsx(_components.a, {
            href: "https://dev.emarsys.com/docs/emarsys-api/ZG9jOjI0ODk5NzY3-contact-system-fields",
            children: "table"
          }), " to cross reference the field ids.\nYou can also view your settings to reference your custom fields."]
        }), "\n", _jsxs(_components.li, {
          children: ["Any fields with a date type are formatted: ", _jsx(_components.em, {
            children: "YYYY-MM-DD"
          }), ". You can have your date values be a string in this format (for example, \"2020-11-30\")."]
        }), "\n", _jsxs(_components.li, {
          children: ["You must use the Emarsys accepted values for any choice fields (that is, ", _jsx(_components.code, {
            children: "gender"
          }), ", ", _jsx(_components.code, {
            children: "title"
          }), ", etc.)."]
        }), "\n"]
      }), "\n", _jsx(_components.h2, {
        id: "debugging",
        children: "Debugging"
      }), "\n", _jsxs(_components.p, {
        children: ["Similarly to the fields mapping in the sync configuration, the request will only show the fields' ids unless it's Emarsys' internal ", _jsx(_components.code, {
          children: "id"
        }), " and ", _jsx(_components.code, {
          children: "uid"
        }), ".\nPlease refer to this ", _jsx(_components.a, {
          href: "https://dev.emarsys.com/docs/emarsys-api/ZG9jOjI0ODk5NzY3-contact-system-fields",
          children: "table"
        }), " and your custom fields settings to cross reference\nthe ID to the field's name."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-emarsys-request.png",
          alt: ""
        })
      }), "\n", _jsxs(_components.p, {
        children: ["The Emarsys API lets us upsert/update/remove contacts in batches per API call. Most cases, the API will let us know which specific rows fail, instead of failing\nthe entire batch. However, the error message may not be very descriptive but it will reference their ", _jsx(_components.a, {
          href: "https://dev.emarsys.com/docs/emarsys-api/ZG9jOjI0ODk5NzY4-http-200-errors",
          children: "internal error code"
        }), "."]
      })]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
